import type { Server } from '@forest/paywall-core'
import type { JumpPointConfig } from '~/config'
import type { ActivityRange } from '~/types/game-pass'
import { isAfterEarningWindow } from './is-after-earning-window'

export interface JumpPointContext {
  /**
   * Bounds are treated inclusively; the range's exclude_* flags are
   * intentionally ignored.
   */
  validRange: Pick<ActivityRange, 'lower' | 'upper'>
  accumulatedScore: number
  server: Server
  /** Server time in ms — never device time. */
  now: number
}

export interface VisibleJumpPoint extends JumpPointConfig {
  status: 'purchasable' | 'locked'
  /** Server-time ms when this point becomes (or became) purchasable. */
  opensAt: number
}

const DAY_MS = 24 * 60 * 60 * 1000
const JUMP_WINDOW_OPEN_DELAY_MS = 14 * DAY_MS
export const JUMP_POINT_STAGGER_MS = 7 * DAY_MS

/**
 * The jump points currently shown on the track, each tagged purchasable or
 * locked. Points unlock on a stagger: sorted by required_score, point i
 * opens 14 + 7i days after the earning window opens. The schedule is a
 * property of the event config alone — a point's position in the full
 * sorted list fixes its opensAt, no matter which points the user's
 * progress or the active server hides.
 *
 * Nothing shows before the first unlock (+14d) or once the event turns
 * claim-only; locked points hide under the same per-point rules as
 * purchasable ones (score reached, no product for the server).
 */
export function getVisibleJumpPoints(
  jumpPoints: JumpPointConfig[] | undefined,
  context: JumpPointContext,
): VisibleJumpPoint[] {
  if (!jumpPoints || jumpPoints.length === 0) return []
  const { validRange, accumulatedScore, server, now } = context
  const windowOpensAt =
    new Date(validRange.lower).getTime() + JUMP_WINDOW_OPEN_DELAY_MS
  if (now < windowOpensAt || isAfterEarningWindow(validRange, now)) return []
  return [...jumpPoints]
    .sort((a, b) => a.required_score - b.required_score)
    .map((jumpPoint, index): VisibleJumpPoint => {
      const opensAt = windowOpensAt + index * JUMP_POINT_STAGGER_MS
      return {
        ...jumpPoint,
        opensAt,
        status: now >= opensAt ? 'purchasable' : 'locked',
      }
    })
    .filter(
      (jumpPoint) =>
        // Product id 0 is real config data — only a missing key hides a point.
        jumpPoint.product_id[server] != null &&
        accumulatedScore < jumpPoint.required_score,
    )
}
